import { Button } from '@/components/ui/button';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { getApiErrorMessage } from '@/lib/api/error';
import {
  UserNotificationPreferencesService,
  userNotificationPreferencesQueryKey,
} from '@/services/user-notification-preferences.service';
import { useQueryClient } from '@tanstack/react-query';
import { BellOff, BellRing, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function PushNotificationSettings() {
  const queryClient = useQueryClient();
  const { state, enablePush, disablePush } = usePushNotifications();

  const enable = async () => {
    try {
      if (await enablePush()) {
        const preferences = await UserNotificationPreferencesService.update({ pushEnabled: true });
        queryClient.setQueryData(userNotificationPreferencesQueryKey, preferences);
        toast.success('Notificações em tempo real ativadas');
      }
    } catch (error) {
      toast.error('Não foi possível ativar', {
        description: getApiErrorMessage(error, 'Confira as configurações do navegador.'),
      });
    }
  };

  const disable = async () => {
    try {
      const preferences = await UserNotificationPreferencesService.update({ pushEnabled: false });
      queryClient.setQueryData(userNotificationPreferencesQueryKey, preferences);
      await disablePush();
      toast.success('Notificações em tempo real desativadas');
    } catch (error) {
      toast.error('Não foi possível desativar', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    }
  };

  const isLoading = state === 'checking' || state === 'loading';
  const isSubscribed = state === 'subscribed';

  return (
    <section className="mb-6 flex flex-col gap-4 rounded-2xl border bg-white p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
      <div className="flex gap-3">
        <span className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
          {state === 'denied' ? <BellOff className="h-5 w-5" /> : <BellRing className="h-5 w-5" />}
        </span>
        <div>
          <h2 className="text-sm font-semibold">Notificações em tempo real</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            {isSubscribed && 'Ativas neste dispositivo.'}
            {state === 'idle' && 'Receba avisos mesmo quando o app estiver fechado.'}
            {state === 'denied' && 'Bloqueadas nas configurações deste navegador.'}
            {state === 'unsupported' && 'Este navegador não oferece suporte a Web Push.'}
            {state === 'error' && 'Não foi possível consultar a configuração atual.'}
            {isLoading && 'Verificando este dispositivo…'}
          </p>
        </div>
      </div>

      {isSubscribed ? (
        <Button variant="outline" size="sm" onClick={() => void disable()}>
          Desativar Push
        </Button>
      ) : state === 'idle' || state === 'error' ? (
        <Button size="sm" onClick={() => void enable()}>
          Ativar notificações
        </Button>
      ) : isLoading ? (
        <Loader2
          className="text-muted-foreground h-5 w-5 animate-spin self-center"
          aria-label="Verificando notificações"
        />
      ) : null}
    </section>
  );
}
